import type { UpdateOrganisationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateOrganisationScimInput {
  ScimEnabled: boolean;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpdateOrganisationMutation => ({
  update_auth_organisation: {
    __typename: 'auth_organisation_mutation_response',
    affected_rows: data.affected_rows,
  },
});

export const useUpdateOrganisationScimTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.organisation.updateScim.mutationOptions({
      onSuccess: async () => {
        // Invalidate any organisation-related tRPC queries. The legacy
        // GraphQL `GetOrganisationDocument` is refetched manually via
        // Apollo `refetch()` in the consumer; this covers any future
        // tRPC organisation queries.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.organisation.pathKey(),
        });
      },
    }),
  });

  return {
    updateOrganisationScim: async (
      input: UpdateOrganisationScimInput
    ): Promise<UpdateOrganisationMutation> => {
      const result = await mutation.mutateAsync({
        ScimEnabled: input.ScimEnabled,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
